import { useState, useEffect } from 'react';
import { updateFooterSetting } from '../../../../services/settingService';

interface FooterSettingsProps {
  settings: any;
  onUpdate: (settings: any) => void;
}

interface Link {
  label: string;
  url: string;
}

interface Column {
  title: string;
  links: Link[];
}

const FooterSettings = ({ settings, onUpdate }: FooterSettingsProps) => {
  const [formData, setFormData] = useState({
    about: '',
    columns: [] as Column[],
    contact: {
      country: '',
      phone: '',
      emails: [] as string[],
      workingHours: [] as string[]
    },
    copyright: '',
    poweredBy: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (settings?.footer) {
      const columns = settings.footer.columns || [];
      // Đảm bảo có đúng 2 cột
      const normalizedColumns: Column[] = [];
      if (columns.length === 0) {
        normalizedColumns.push({ title: '', links: [] }, { title: '', links: [] });
      } else if (columns.length === 1) {
        normalizedColumns.push(columns[0], { title: '', links: [] });
      } else {
        normalizedColumns.push(...columns.slice(0, 2));
      }

      const contactEmails = settings.footer.contact?.emails || [];
      // Chỉ lấy email đầu tiên
      const normalizedEmails = contactEmails.length > 0 ? [contactEmails[0]] : [''];

      setFormData({
        about: settings.footer.about || '',
        columns: normalizedColumns,
        contact: {
          country: settings.footer.contact?.country || '',
          phone: settings.footer.contact?.phone || '',
          emails: normalizedEmails,
          workingHours: Array.isArray(settings.footer.contact?.workingHours) 
            ? settings.footer.contact.workingHours 
            : (settings.footer.contact?.workingHours ? [settings.footer.contact.workingHours] : [])
        },
        copyright: settings.footer.copyright || '',
        poweredBy: settings.footer.poweredBy || ''
      });
    }
  }, [settings]);

  const validateForm = (): string | null => {
    // Kiểm tra links - nếu có link thì phải có cả label và url
    for (let i = 0; i < formData.columns.length; i++) {
      const column = formData.columns[i];
      for (let j = 0; j < column.links.length; j++) {
        const link = column.links[j];
        if (link.label || link.url) {
          // Nếu có một trong hai thì phải có cả hai
          if (!link.label || !link.label.trim()) {
            return `Cột ${i + 1}: Link ${j + 1} - Vui lòng nhập tên link`;
          }
          if (!link.url || !link.url.trim()) {
            return `Cột ${i + 1}: Link ${j + 1} - Vui lòng nhập đường dẫn link`;
          }
        }
      }
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Validate form trước khi submit
    const validationError = validateForm();
    if (validationError) {
      setMessage({
        type: 'error',
        text: validationError
      });
      setLoading(false);
      return;
    }

    // Lọc bỏ các link rỗng (không có cả label và url)
    const cleanedFormData = {
      ...formData,
      columns: formData.columns.map(column => ({
        ...column,
        links: column.links.filter(link => link.label && link.label.trim() && link.url && link.url.trim())
      }))
    };

    try {
      const response = await updateFooterSetting(cleanedFormData);
      if (response.success) {
        setMessage({ type: 'success', text: response.message || 'Cập nhật thành công' });
        if (onUpdate && response.data) {
          onUpdate(response.data);
        }
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật'
      });
    } finally {
      setLoading(false);
    }
  };

  const addColumn = () => {
    // Chỉ cho phép thêm nếu chưa đủ 2 cột
    if (formData.columns.length < 2) {
      setFormData({
        ...formData,
        columns: [...formData.columns, { title: '', links: [] }]
      });
    }
  };

  const removeColumn = (index: number) => {
    // Không cho phép xóa nếu chỉ còn 2 cột (tối thiểu 2 cột)
    if (formData.columns.length > 2) {
      const newColumns = formData.columns.filter((_, i) => i !== index);
      setFormData({ ...formData, columns: newColumns });
    }
  };

  const updateColumn = (index: number, field: string, value: string) => {
    const newColumns = [...formData.columns];
    newColumns[index] = { ...newColumns[index], [field]: value };
    setFormData({ ...formData, columns: newColumns });
  };

  const addLink = (columnIndex: number) => {
    const newColumns = [...formData.columns];
    newColumns[columnIndex].links = [...newColumns[columnIndex].links, { label: '', url: '' }];
    setFormData({ ...formData, columns: newColumns });
  };

  const removeLink = (columnIndex: number, linkIndex: number) => {
    const newColumns = [...formData.columns];
    newColumns[columnIndex].links = newColumns[columnIndex].links.filter((_, i) => i !== linkIndex);
    setFormData({ ...formData, columns: newColumns });
  };

  const updateLink = (columnIndex: number, linkIndex: number, field: string, value: string) => {
    const newColumns = [...formData.columns];
    newColumns[columnIndex].links[linkIndex] = {
      ...newColumns[columnIndex].links[linkIndex],
      [field]: value
    };
    setFormData({ ...formData, columns: newColumns });
  };

  const updateContactEmail = (value: string) => {
    // Chỉ cho phép 1 email
    setFormData({
      ...formData,
      contact: { ...formData.contact, emails: [value] }
    });
  };

  const addWorkingHour = () => {
    setFormData({
      ...formData,
      contact: {
        ...formData.contact,
        workingHours: [...formData.contact.workingHours, '']
      }
    });
  };

  const removeWorkingHour = (index: number) => {
    const newWorkingHours = formData.contact.workingHours.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      contact: { ...formData.contact, workingHours: newWorkingHours }
    });
  };

  const updateWorkingHour = (index: number, value: string) => {
    const newWorkingHours = [...formData.contact.workingHours];
    newWorkingHours[index] = value;
    setFormData({
      ...formData,
      contact: { ...formData.contact, workingHours: newWorkingHours }
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-xl font-bold text-gray-900 mb-6">Cài đặt Footer</h3>
      
      {message.text && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* About */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Mô tả Footer (About)
            </label>
            <span className={`text-sm ${
              formData.about.length > 100 
                ? 'text-red-600 font-semibold' 
                : 'text-gray-500'
            }`}>
              {formData.about.length}/100
            </span>
          </div>
          <textarea
            rows={4}
            value={formData.about}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length <= 100) {
                setFormData({ ...formData, about: value });
              }
            }}
            maxLength={100}
            placeholder="Mô tả ngắn về công ty..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent resize-none"
          />
          <p className="text-sm text-gray-500 mt-1">Tối đa 100 ký tự</p>
        </div>

        {/* Columns */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Cột Footer (Columns) - Tối đa 2 cột
            </label>
            {formData.columns.length < 2 && (
              <button
                type="button"
                onClick={addColumn}
                className="px-4 py-2 bg-[#2A4B7C] hover:bg-[#2A4B7C]/90 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <i className="ri-add-line mr-1"></i>
                Thêm cột
              </button>
            )}
          </div>

          {formData.columns.map((column, columnIndex) => (
            <div key={columnIndex} className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 mr-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tiêu đề cột {columnIndex + 1}
                  </label>
                  <input
                    type="text"
                    value={column.title}
                    onChange={(e) => updateColumn(columnIndex, 'title', e.target.value)}
                    placeholder="Ví dụ: Dịch vụ, Công ty, Liên hệ"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                  />
                </div>
                {formData.columns.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removeColumn(columnIndex)}
                    className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm transition-colors"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Links</label>
                  <button
                    type="button"
                    onClick={() => addLink(columnIndex)}
                    className="px-3 py-1 bg-[#00C897] hover:bg-[#00C897]/90 text-white rounded text-sm transition-colors"
                  >
                    <i className="ri-add-line mr-1"></i>
                    Thêm link
                  </button>
                </div>

                {column.links.map((link, linkIndex) => (
                  <div key={linkIndex} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={link.label}
                      onChange={(e) => updateLink(columnIndex, linkIndex, 'label', e.target.value)}
                      placeholder="Tên link"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={link.url}
                      onChange={(e) => updateLink(columnIndex, linkIndex, 'url', e.target.value)}
                      placeholder="/path hoặc https://..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => removeLink(columnIndex, linkIndex)}
                      className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {formData.columns.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">
              Đang khởi tạo 2 cột footer...
            </p>
          )}
          {formData.columns.length < 2 && formData.columns.length > 0 && (
            <p className="text-sm text-yellow-600 text-center py-2">
              Cần ít nhất 2 cột. Nhấn "Thêm cột" để thêm cột thứ 2.
            </p>
          )}
        </div>

        {/* Contact */}
        <div className="border-t pt-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Thông tin liên hệ Footer</h4>
          
          <div className="grid md:grid-cols-2 gap-6 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quốc gia
              </label>
              <input
                type="text"
                value={formData.contact.country}
                onChange={(e) => setFormData({
                  ...formData,
                  contact: { ...formData.contact, country: e.target.value }
                })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Số điện thoại
              </label>
              <input
                type="text"
                value={formData.contact.phone}
                onChange={(e) => setFormData({
                  ...formData,
                  contact: { ...formData.contact, phone: e.target.value }
                })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.contact.emails[0] || ''}
              onChange={(e) => updateContactEmail(e.target.value)}
              placeholder="email@example.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Giờ làm việc
              </label>
              <button
                type="button"
                onClick={addWorkingHour}
                className="px-3 py-1 bg-[#00C897] hover:bg-[#00C897]/90 text-white rounded text-sm transition-colors"
              >
                <i className="ri-add-line mr-1"></i>
                Thêm giờ làm việc
              </button>
            </div>
            {formData.contact.workingHours.map((hour, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={hour}
                  onChange={(e) => updateWorkingHour(index, e.target.value)}
                  placeholder="Ví dụ: Thứ 2 - Thứ 6: 8:00 - 17:00"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => removeWorkingHour(index)}
                  className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            ))}
            {formData.contact.workingHours.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                Chưa có giờ làm việc nào. Nhấn "Thêm giờ làm việc" để bắt đầu.
              </p>
            )}
          </div>
        </div>

        {/* Copyright & Powered By */}
        <div className="border-t pt-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Copyright
              </label>
              <input
                type="text"
                value={formData.copyright}
                onChange={(e) => setFormData({ ...formData, copyright: e.target.value })}
                placeholder="© 2024 Công ty ABC"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Powered By
              </label>
              <input
                type="text"
                value={formData.poweredBy}
                onChange={(e) => setFormData({ ...formData, poweredBy: e.target.value })}
                placeholder="Powered by..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="pt-4 border-t">
          <button
            type="submit"
            disabled={loading}
            className="bg-[#2A4B7C] hover:bg-[#2A4B7C]/90 disabled:opacity-50 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg font-semibold transition-colors"
          >
            {loading ? 'Đang lưu...' : 'Lưu cài đặt Footer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FooterSettings;

